<template>
  <AppShell
    active="utilities"
    :workspace="workspace"
    title="Utility Directory"
    subtitle="Serving-utility identities, HIFLD territory facts, and curated diligence profile fields."
    max-width-class="max-w-none"
    data-testid="utilities-page"
  >
    <template #actions>
      <a href="/map" class="hidden h-10 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted sm:inline-flex">
        <MapPinned class="size-4" aria-hidden="true" />
        Map
      </a>
      <button
        type="button"
        class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-wait disabled:opacity-70"
        :disabled="isImporting"
        @click="importDirectory"
      >
        <LoaderCircle v-if="isImporting" class="size-4 animate-spin" aria-hidden="true" />
        <RefreshCw v-else class="size-4" aria-hidden="true" />
        Refresh
      </button>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="rounded-md border border-land-border bg-land-surface shadow-sm" aria-label="Utility directory metrics">
      <div class="utilities-metric-grid">
        <article v-for="metric in metricCards" :key="metric.key" class="min-w-0 px-4 py-3">
          <p class="truncate text-xs font-medium text-land-ink-soft">{{ metric.label }}</p>
          <div class="mt-1 flex items-baseline gap-2">
            <p class="text-xl font-semibold tracking-normal">{{ metric.value }}</p>
            <p class="truncate text-xs text-land-ink-muted">{{ metric.detail }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="mt-4 rounded-md border border-land-border bg-land-surface shadow-sm">
      <div class="utilities-filter-grid border-b border-land-border-soft p-3">
        <label class="relative block">
          <span class="sr-only">Search utilities</span>
          <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-land-ink-soft" aria-hidden="true" />
          <input v-model="state.q" type="search" placeholder="Search utility, holding co., EIA ID" class="h-10 w-full rounded-md border border-land-border bg-white py-2 pl-9 pr-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
        </label>

        <label class="grid h-10 grid-cols-[54px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">Type</span>
          <select v-model="state.type" aria-label="Filter by utility type" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="type in filters.utilityTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
          </select>
        </label>

        <label class="grid h-10 grid-cols-[54px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">State</span>
          <select v-model="state.state" aria-label="Filter by state" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="state in filters.states" :key="state" :value="state">{{ state }}</option>
          </select>
        </label>

        <label class="grid h-10 grid-cols-[62px_minmax(0,1fr)] items-center rounded-md border border-land-border bg-white">
          <span class="border-r border-land-border-soft px-3 text-xs font-semibold uppercase text-land-ink-soft">ISO</span>
          <select v-model="state.iso" aria-label="Filter by ISO/RTO" class="h-full min-w-0 rounded-r-md border-0 bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-land-primary/20">
            <option value="all">All</option>
            <option v-for="iso in filters.isoRtos" :key="iso" :value="iso">{{ iso }}</option>
          </select>
        </label>

        <div class="flex min-w-0 flex-nowrap items-center justify-end gap-2">
          <button
            type="button"
            class="inline-flex h-10 items-center rounded-md border px-3 text-sm font-semibold"
            :class="state.curated ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
            @click="state.curated = !state.curated"
          >
            Curated
          </button>
          <div class="inline-flex h-10 rounded-md border border-land-border bg-white p-1">
            <button
              v-for="mode in sortModes"
              :key="mode.value"
              type="button"
              class="inline-flex items-center gap-1 rounded px-2.5 text-xs font-semibold"
              :class="state.sort === mode.value ? 'bg-land-primary-soft text-land-primary' : 'text-land-ink-muted hover:bg-land-surface-muted'"
              @click="setSort(mode.value)"
            >
              {{ mode.label }}
              <ArrowUp v-if="state.sort === mode.value && state.direction === 'asc'" class="size-3" aria-hidden="true" />
              <ArrowDown v-else-if="state.sort === mode.value" class="size-3" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-land-border-soft px-4 py-2">
        <p class="text-sm font-semibold">
          <LoaderCircle v-if="isLoading" class="mr-1 inline size-3.5 animate-spin align-[-2px]" aria-hidden="true" />
          {{ formatNumber(pagination.totalCount) }} utilities
        </p>
        <button v-if="hasActiveFilters" type="button" class="text-sm font-semibold text-land-primary hover:text-land-primary-hover" @click="clearFilters">
          Clear filters
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full min-w-[980px] text-left text-sm">
          <thead class="bg-land-surface-muted text-xs font-semibold uppercase text-land-ink-soft">
            <tr>
              <th scope="col" class="px-4 py-3">Utility</th>
              <th scope="col" class="px-4 py-3">Territory</th>
              <th scope="col" class="px-4 py-3">Customers</th>
              <th scope="col" class="px-4 py-3">Profile</th>
              <th scope="col" class="px-4 py-3 text-right">Open</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-land-border-soft">
            <tr
              v-for="utility in utilities"
              :key="utility.id"
              class="cursor-pointer bg-white hover:bg-land-surface-muted"
              :class="selectedUtility?.id === utility.id && detailOpen ? 'bg-land-primary-soft/60' : ''"
              @click="openUtility(utility, $event)"
            >
              <td class="px-4 py-3">
                <div class="min-w-0">
                  <p class="truncate font-semibold text-land-ink">{{ utility.name }}</p>
                  <p class="mt-1 truncate text-xs text-land-ink-soft">{{ utility.holdingCompany || 'Independent / unknown holding company' }}</p>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge :label="utility.utilityTypeLabel || 'Unknown type'" :tone="utilityTypeTone(utility.utilityType)" />
                  <StateBadge v-if="utility.isoRto" :label="utility.isoRto" tone="blue" />
                  <StateBadge v-else :label="utility.balancingAuthority || 'Unknown BA'" tone="slate" />
                  <StateBadge :label="compactList(utility.states).slice(0, 3).join(', ') || 'No state'" tone="slate" />
                </div>
              </td>
              <td class="px-4 py-3 font-semibold text-land-ink">{{ compactNumber(utility.customerCount) }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge v-if="utility.curatedProfile" label="Curated" tone="green" />
                  <StateBadge v-else label="Open" tone="amber" />
                  <StateBadge v-if="utility.regulated === true" label="Regulated" tone="blue" />
                  <StateBadge v-else-if="utility.regulated === false" label="Deregulated" tone="amber" />
                </div>
              </td>
              <td class="px-4 py-3 text-right">
                <button type="button" class="inline-flex size-8 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted" :aria-label="`Open ${utility.name}`" @click.stop="openUtility(utility, $event)">
                  <ArrowRight class="size-4" aria-hidden="true" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.totalCount > 0" class="flex flex-wrap items-center justify-between gap-3 border-t border-land-border-soft p-3">
        <div class="flex items-center gap-2 text-sm text-land-ink-muted">
          <span>Showing {{ formatNumber(rangeStart) }}–{{ formatNumber(rangeEnd) }} of {{ formatNumber(pagination.totalCount) }}</span>
          <label class="ml-2 inline-flex items-center gap-1">
            <span class="text-xs uppercase text-land-ink-soft">Per page</span>
            <select v-model.number="state.perPage" aria-label="Results per page" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-land-primary/20">
              <option v-for="size in perPageOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </label>
        </div>
        <nav class="inline-flex items-center gap-1" aria-label="Pagination">
          <button type="button" class="inline-flex size-8 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="pagination.page <= 1" aria-label="Previous page" @click="goToPage(pagination.page - 1)">
            <ChevronLeft class="size-4" aria-hidden="true" />
          </button>
          <button
            v-for="number in pageNumbers"
            :key="number"
            type="button"
            class="inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-semibold"
            :class="number === pagination.page ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
            :aria-current="number === pagination.page ? 'page' : undefined"
            @click="goToPage(number)"
          >
            {{ number }}
          </button>
          <button type="button" class="inline-flex size-8 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="pagination.page >= totalPages" aria-label="Next page" @click="goToPage(pagination.page + 1)">
            <ChevronRight class="size-4" aria-hidden="true" />
          </button>
        </nav>
      </div>

      <div v-if="!isLoading && utilities.length === 0" class="px-4 py-12 text-center">
        <p class="text-sm font-semibold">No matching utilities</p>
        <p class="mt-2 text-sm text-land-ink-muted">Adjust filters or refresh the directory.</p>
      </div>
    </section>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="detailOpen && selectedUtility" class="fixed inset-0 z-50 bg-slate-950/35" role="presentation" @click.self="closeDetail">
        <aside ref="detailPanel" tabindex="-1" class="ml-auto flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl" role="dialog" aria-modal="true" :aria-label="`${selectedUtility.name} utility profile`">
          <header class="border-b border-land-border-soft bg-white p-5">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="flex flex-wrap gap-1.5">
                  <StateBadge :label="selectedUtility.utilityTypeLabel || 'Unknown type'" :tone="utilityTypeTone(selectedUtility.utilityType)" />
                  <StateBadge v-if="selectedUtility.isoRto" :label="selectedUtility.isoRto" tone="blue" />
                  <StateBadge v-if="selectedUtility.curatedProfile" label="Curated profile" tone="green" />
                </div>
                <h2 class="mt-3 text-2xl font-semibold tracking-normal">{{ selectedUtility.name }}</h2>
                <p class="mt-2 text-sm text-land-ink-muted">{{ selectedUtility.holdingCompany || 'Holding company not available' }}</p>
              </div>
              <button type="button" class="grid size-9 shrink-0 place-items-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted" aria-label="Close utility profile" @click="closeDetail">
                <X class="size-4" aria-hidden="true" />
              </button>
            </div>

            <div class="mt-4 flex flex-wrap gap-2">
              <a v-if="selectedUtility.website" :href="selectedUtility.website" target="_blank" rel="noreferrer" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                <ExternalLink class="size-4" aria-hidden="true" />
                Website
              </a>
              <a v-if="selectedUtility.phone" :href="`tel:${selectedUtility.phone}`" class="inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                <Phone class="size-4" aria-hidden="true" />
                {{ selectedUtility.phone }}
              </a>
            </div>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto">
            <section class="grid gap-4 p-5 md:grid-cols-2">
              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Directory Facts</h3>
                <dl class="mt-3 grid gap-2">
                  <div v-for="fact in directoryFacts" :key="fact.label" class="grid grid-cols-[150px_minmax(0,1fr)] gap-3 rounded-md border border-land-border-soft bg-land-surface-muted px-3 py-2">
                    <dt class="text-xs font-medium text-land-ink-soft">{{ fact.label }}</dt>
                    <dd class="min-w-0 truncate text-sm font-semibold text-land-ink">{{ fact.value }}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Load & Service Metrics</h3>
                <dl class="mt-3 grid gap-2">
                  <div v-for="metric in selectedMetrics" :key="metric.label" class="grid grid-cols-[150px_minmax(0,1fr)] gap-3 rounded-md border border-land-border-soft bg-land-surface-muted px-3 py-2">
                    <dt class="text-xs font-medium text-land-ink-soft">{{ metric.label }}</dt>
                    <dd class="min-w-0 truncate text-sm font-semibold text-land-ink">{{ metric.value }}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section class="border-t border-land-border-soft p-5">
              <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Large-Load Profile</h3>
              <div class="mt-3 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-sm font-semibold">{{ largeLoadTariff.has ? 'Large-load tariff identified' : 'Large-load tariff not curated' }}</p>
                    <p class="mt-2 text-sm leading-6 text-land-ink-muted">{{ largeLoadTariff.summary || 'No curated summary is stored for this utility.' }}</p>
                  </div>
                  <StateBadge :label="largeLoadTariff.has ? 'Yes' : 'Open'" :tone="largeLoadTariff.has ? 'green' : 'amber'" />
                </div>
                <a v-if="largeLoadTariff.url" :href="largeLoadTariff.url" target="_blank" rel="noreferrer" class="mt-4 inline-flex h-9 items-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                  <ExternalLink class="size-4" aria-hidden="true" />
                  Tariff source
                </a>
              </div>
            </section>

            <section class="grid gap-4 border-t border-land-border-soft p-5 md:grid-cols-2">
              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Utility Links</h3>
                <div class="mt-3 grid gap-2">
                  <a v-if="profile.hosting_capacity_map_url" :href="profile.hosting_capacity_map_url" target="_blank" rel="noreferrer" class="inline-flex min-h-10 items-center justify-between gap-3 rounded-md border border-land-border-soft bg-white px-3 py-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                    Hosting capacity map
                    <ExternalLink class="size-4" aria-hidden="true" />
                  </a>
                  <a v-if="profile.interconnection_url" :href="profile.interconnection_url" target="_blank" rel="noreferrer" class="inline-flex min-h-10 items-center justify-between gap-3 rounded-md border border-land-border-soft bg-white px-3 py-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted">
                    Interconnection portal
                    <ExternalLink class="size-4" aria-hidden="true" />
                  </a>
                  <div v-if="!profile.hosting_capacity_map_url && !profile.interconnection_url" class="rounded-md border border-land-border-soft bg-white p-3 text-sm text-land-ink-muted">
                    No hosting-capacity or interconnection links are curated.
                  </div>
                </div>
              </div>

              <div>
                <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Economic Development Contact</h3>
                <div class="mt-3 rounded-md border border-land-border-soft bg-white p-4">
                  <p class="text-sm font-semibold">{{ economicDevelopment.name || 'Not curated' }}</p>
                  <p v-if="economicDevelopment.title" class="mt-1 text-sm text-land-ink-muted">{{ economicDevelopment.title }}</p>
                  <div class="mt-3 flex flex-wrap gap-2">
                    <a v-if="economicDevelopment.email" :href="`mailto:${economicDevelopment.email}`" class="inline-flex h-8 items-center gap-2 rounded-md border border-land-border bg-white px-2.5 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted">
                      <Mail class="size-3.5" aria-hidden="true" />
                      {{ economicDevelopment.email }}
                    </a>
                    <a v-if="economicDevelopment.phone" :href="`tel:${economicDevelopment.phone}`" class="inline-flex h-8 items-center gap-2 rounded-md border border-land-border bg-white px-2.5 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted">
                      <Phone class="size-3.5" aria-hidden="true" />
                      {{ economicDevelopment.phone }}
                    </a>
                    <a v-if="economicDevelopment.url" :href="economicDevelopment.url" target="_blank" rel="noreferrer" class="inline-flex h-8 items-center gap-2 rounded-md border border-land-border bg-white px-2.5 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted">
                      <ExternalLink class="size-3.5" aria-hidden="true" />
                      Contact page
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <section class="border-t border-land-border-soft p-5">
              <h3 class="text-sm font-semibold uppercase text-land-ink-soft">Source Provenance</h3>
              <div class="mt-3 grid gap-3 md:grid-cols-3">
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-medium text-land-ink-soft">Source</p>
                  <p class="mt-1 truncate text-sm font-semibold">{{ selectedUtility.source || 'Unknown' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-medium text-land-ink-soft">Vintage</p>
                  <p class="mt-1 truncate text-sm font-semibold">{{ selectedUtility.dataVintage || 'Unknown' }}</p>
                </div>
                <div class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-medium text-land-ink-soft">Updated</p>
                  <p class="mt-1 truncate text-sm font-semibold">{{ formatDateTime(selectedUtility.updatedAt) }}</p>
                </div>
              </div>
              <details class="mt-3 rounded-md border border-land-border-soft bg-white">
                <summary class="cursor-pointer px-3 py-2 text-sm font-semibold text-land-ink-muted">Raw HIFLD attributes</summary>
                <pre class="max-h-72 overflow-auto border-t border-land-border-soft p-3 text-xs leading-5 text-land-ink-muted">{{ JSON.stringify(selectedUtility.raw || {}, null, 2) }}</pre>
              </details>
            </section>
          </div>
        </aside>
      </div>
    </Transition>
  </AppShell>
</template>

<script setup>
import { ArrowDown, ArrowRight, ArrowUp, ChevronLeft, ChevronRight, ExternalLink, LoaderCircle, Mail, MapPinned, Phone, RefreshCw, Search, X } from '@lucide/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'
import AppShell from '@/components/shared/app_shell.vue'
import StateBadge from '@/components/shared/state_badge.vue'
import { DEFAULT_DIRECTION, DEFAULT_STATE, PER_PAGE_OPTIONS, pageWindow, paramsFromState, stateFromQuery } from './directory_params'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

const payload = ref(props.payload)
const utilities = ref([...(props.payload.utilities || [])])
const pagination = ref(props.payload.pagination || { page: 1, perPage: DEFAULT_STATE.perPage, totalCount: 0, totalPages: 1 })
const filters = computed(() => payload.value.filters || { states: [], isoRtos: [], utilityTypes: [] })
const metrics = computed(() => payload.value.metrics || {})
const workspace = computed(() => payload.value.workspace || {})

const state = reactive(stateFromQuery(globalThis.location?.search || ''))
const isLoading = ref(false)
const isImporting = ref(false)
const notice = ref('')
const errors = ref([])
const selectedUtilityId = ref(null)
const detailOpen = ref(false)
const lastFocusedRow = ref(null)
const detailPanel = ref(null)

const sortModes = [
  { value: 'customers', label: 'Customers' },
  { value: 'name', label: 'Name' },
  { value: 'curated', label: 'Curated' }
]
const perPageOptions = PER_PAGE_OPTIONS

const metricCards = computed(() => [
  { key: 'total', label: 'Utilities', value: formatNumber(metrics.value.totalUtilities), detail: `${formatNumber(metrics.value.states)} states` },
  { key: 'customers', label: 'Customers', value: compactNumber(metrics.value.totalCustomers), detail: 'HIFLD' },
  { key: 'iou', label: 'IOUs', value: formatNumber(metrics.value.investorOwned), detail: 'Investor-owned' },
  { key: 'public', label: 'Public power', value: formatNumber(metrics.value.publicPower), detail: 'Muni/federal' },
  { key: 'coop', label: 'Co-ops', value: formatNumber(metrics.value.cooperative), detail: 'Co-op' },
  { key: 'curated', label: 'Curated', value: formatNumber(metrics.value.curatedProfiles), detail: 'Profiles' }
])

const hasActiveFilters = computed(() => Boolean(
  state.q.trim() ||
    state.type !== 'all' ||
    state.state !== 'all' ||
    state.iso !== 'all' ||
    state.curated
))

const totalPages = computed(() => pagination.value.totalPages || 1)
const pageNumbers = computed(() => pageWindow(pagination.value.page, totalPages.value))
const rangeStart = computed(() => pagination.value.totalCount === 0 ? 0 : (pagination.value.page - 1) * pagination.value.perPage + 1)
const rangeEnd = computed(() => Math.min(pagination.value.page * pagination.value.perPage, pagination.value.totalCount))

const selectedUtility = computed(() => utilities.value.find((utility) => utility.id === selectedUtilityId.value) || null)
const profile = computed(() => selectedUtility.value?.diligenceProfile || {})
const largeLoadTariff = computed(() => profile.value.large_load_tariff || profile.value.largeLoadTariff || {})
const economicDevelopment = computed(() => profile.value.economic_development || profile.value.economicDevelopment || {})

const directoryFacts = computed(() => {
  const utility = selectedUtility.value
  if (!utility) return []

  return [
    { label: 'EIA Utility ID', value: utility.eiaUtilityId || 'Not available' },
    { label: 'Type', value: utility.utilityTypeLabel || 'Unknown' },
    { label: 'States', value: compactList(utility.states).join(', ') || 'Unknown' },
    { label: 'Regulated', value: regulatedLabel(utility.regulated) },
    { label: 'ISO/RTO', value: utility.isoRto || 'Unknown' },
    { label: 'Balancing Authority', value: utility.balancingAuthority || 'Unknown' },
    { label: 'NERC / Planning Region', value: utility.nercRegion || 'Unknown' },
    { label: 'Holding Company', value: utility.holdingCompany || 'Unknown' }
  ]
})

const selectedMetrics = computed(() => {
  const utility = selectedUtility.value
  if (!utility) return []

  return [
    { label: 'Customers', value: compactNumber(utility.customerCount) },
    { label: 'Summer Peak MW', value: compactNumber(metricValue(utility, 'summerPeakMw')) },
    { label: 'Winter Peak MW', value: compactNumber(metricValue(utility, 'winterPeakMw')) },
    { label: 'Summer Capacity MW', value: compactNumber(metricValue(utility, 'summerCapacityMw')) },
    { label: 'Winter Capacity MW', value: compactNumber(metricValue(utility, 'winterCapacityMw')) },
    { label: 'Net Generation MWh', value: compactNumber(metricValue(utility, 'netGenerationMwh')) }
  ]
})

let searchTimer = null

const fetchPage = async () => {
  isLoading.value = true
  errors.value = []
  syncUrl()

  try {
    const query = new URLSearchParams(paramsFromState(state)).toString()
    const data = await requestJson(`/utilities.json${query ? `?${query}` : ''}`)
    utilities.value = [...(data.utilities || [])]
    pagination.value = data.pagination || pagination.value
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isLoading.value = false
  }
}

const syncUrl = () => {
  const query = new URLSearchParams(paramsFromState(state)).toString()
  const url = query ? `${globalThis.location.pathname}?${query}` : globalThis.location.pathname
  globalThis.history?.replaceState?.({}, '', url)
}

// Filter/sort/page-size changes reset to page 1 and fetch immediately.
watch(() => [state.type, state.state, state.iso, state.curated, state.sort, state.direction, state.perPage], () => {
  state.page = 1
  fetchPage()
})

// Search is debounced and also resets to page 1.
watch(() => state.q, () => {
  state.page = 1
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(fetchPage, 300)
})

onMounted(() => {
  globalThis.addEventListener?.('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  globalThis.removeEventListener?.('keydown', handleKeydown)
  if (searchTimer) clearTimeout(searchTimer)
  unlockScroll()
})

const setSort = (mode) => {
  if (state.sort === mode) {
    state.direction = state.direction === 'asc' ? 'desc' : 'asc'
  } else {
    state.sort = mode
    state.direction = DEFAULT_DIRECTION[mode]
  }
}

const goToPage = (page) => {
  const clamped = Math.min(Math.max(page, 1), totalPages.value)
  if (clamped !== state.page) {
    state.page = clamped
    fetchPage()
  }
}

const openUtility = (utility, event) => {
  lastFocusedRow.value = event?.currentTarget || null
  selectedUtilityId.value = utility.id
  detailOpen.value = true
  lockScroll()
  nextTick(() => detailPanel.value?.focus())
}

const closeDetail = () => {
  detailOpen.value = false
  unlockScroll()
  lastFocusedRow.value?.focus?.()
}

const lockScroll = () => {
  if (globalThis.document) globalThis.document.body.style.overflow = 'hidden'
}

const unlockScroll = () => {
  if (globalThis.document) globalThis.document.body.style.overflow = ''
}

const trapFocus = (event) => {
  if (event.key !== 'Tab' || !detailPanel.value) return
  const focusable = detailPanel.value.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && globalThis.document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && globalThis.document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

const handleKeydown = (event) => {
  if (!detailOpen.value) return
  if (event.key === 'Escape') closeDetail()
  if (event.key === 'Tab') trapFocus(event)
}

const clearFilters = () => {
  state.q = DEFAULT_STATE.q
  state.type = DEFAULT_STATE.type
  state.state = DEFAULT_STATE.state
  state.iso = DEFAULT_STATE.iso
  state.curated = DEFAULT_STATE.curated
}

const importDirectory = async () => {
  isImporting.value = true
  errors.value = []
  notice.value = ''

  try {
    const data = await requestJson('/utilities/import', { method: 'POST' })
    payload.value = data.payload || payload.value
    utilities.value = [...(payload.value.utilities || [])]
    pagination.value = payload.value.pagination || pagination.value
    selectedUtilityId.value = null
    detailOpen.value = false
    unlockScroll()
    notice.value = `Imported ${formatNumber(data.imported)} utility records.`
  } catch (error) {
    errors.value = normalizeErrors(error)
  } finally {
    isImporting.value = false
  }
}

const compactList = (value) => Array.isArray(value) ? value.filter(Boolean) : []
const metricValue = (utility, key) => utility.metrics?.[key] ?? utility.metrics?.[key.toString()]
const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const compactNumber = (value) => {
  if (value === null || value === undefined || value === '') return 'Unknown'

  return new Intl.NumberFormat(undefined, {
    notation: Math.abs(Number(value)) >= 1_000_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1
  }).format(Number(value))
}
const formatDateTime = (value) => {
  if (!value) return 'Unknown'

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(value))
}
const regulatedLabel = (value) => {
  if (value === true) return 'Regulated'
  if (value === false) return 'Deregulated / unregulated'

  return 'Unknown'
}
const utilityTypeTone = (type) => ({
  investor_owned: 'blue',
  municipal: 'green',
  public_power: 'green',
  cooperative: 'amber',
  state: 'violet',
  federal: 'violet'
})[type] || 'slate'
</script>

<style scoped>
.utilities-metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.utilities-metric-grid > article {
  border-top: 1px solid var(--color-land-border-soft);
}

.utilities-metric-grid > article:nth-child(-n + 2) {
  border-top: 0;
}

.utilities-filter-grid {
  display: grid;
  gap: 0.75rem;
}

@media (min-width: 900px) {
  .utilities-metric-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .utilities-metric-grid > article {
    border-left: 1px solid var(--color-land-border-soft);
    border-top: 0;
  }

  .utilities-metric-grid > article:first-child {
    border-left: 0;
  }

  .utilities-filter-grid {
    align-items: center;
    grid-template-columns: minmax(220px, 1fr) 150px 100px 120px 270px;
  }
}
</style>
